import { openEngineReportPrint } from '@/components/print/openEngineReportPrint';
import type { ReportColumnDef, ReportDefinition } from '@/reports/types';
import type { PrintColumn } from '@/print-engine';
import { formatDate, formatMoney } from '@/utils/format';

function formatPrintCell(value: unknown, format?: string): string {
  if (value == null || value === '') return '—';
  if (format === 'money') {
    const n = Number(value);
    return Number.isFinite(n) ? formatMoney(n) : String(value);
  }
  if (format === 'percent') {
    const n = Number(value);
    return Number.isFinite(n)
      ? `${n.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 0 })}%`
      : String(value);
  }
  if (format === 'date') {
    return formatDate(String(value)) || '—';
  }
  if (format === 'number') {
    const n = Number(value);
    return Number.isFinite(n) ? n.toLocaleString() : String(value);
  }
  const text = String(value).trim();
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(text)) {
    return '—';
  }
  return text || '—';
}

export function printReportTable(input: {
  definition: ReportDefinition;
  columns: ReportColumnDef[];
  rows: Record<string, unknown>[];
  metaLines?: Array<{ label: string; value: string }>;
  /**
   * Wave E report profile orientation.
   * Detail registers with many columns should print landscape.
   */
  landscape?: boolean;
  /** Optional subtitle for the print header (e.g. Customer summary). */
  viewLabel?: string;
}): void {
  // Prefer relative widths so table-layout:fixed shares the page (absolute px sums
  // overflow A4 portrait and clip the last column — e.g. Balance on customer summary).
  const widthBudget = input.columns.reduce((sum, c) => sum + (c.width || 120), 0) || 1;
  const printCols: PrintColumn[] = input.columns.map((c) => {
    const share = Math.max(8, Math.round(((c.width || 120) / widthBudget) * 100));
    return {
      key: c.key,
      label: c.title,
      align: c.align === 'right' ? 'right' : 'left',
      width: `${share}%`,
    };
  });

  const titleBase = input.definition.title.toUpperCase();
  const title = input.viewLabel ? `${titleBase} — ${input.viewLabel.toUpperCase()}` : titleBase;

  openEngineReportPrint({
    docType: input.definition.id,
    title,
    documentNumber: `${input.definition.id}-${new Date().toISOString().slice(0, 10)}`,
    partyName: input.definition.title,
    metaLines: input.metaLines,
    columns: printCols,
    landscape: input.landscape === true,
    lines: input.rows.map((row, i) => {
      const cells = Object.fromEntries(
        input.columns.map((c) => [c.key, formatPrintCell(row[c.dataIndex], c.format)])
      ) as Record<string, string>;
      const first = input.columns[0];
      return {
        lineNumber: i + 1,
        description: first ? formatPrintCell(row[first.dataIndex], first.format) : '—',
        cells,
      };
    }),
  });
}
